import { lazy } from 'react';
import { Spin } from 'antd';
import type { RouteObject } from 'react-router';
import { redirect } from 'react-router';

import { MainLayout } from '../components/layouts/layout';
import { type CurrentUser, setRedirectPath } from '../components/pages/Login/authSlice';
import ProgramDashboardOLHComponent from '../components/pages/program-dashboard-olh/program-dashboard-olh';
import { resetPasswordLoaderWithStore } from '../components/pages/reset-password/reset-password.helper';
import { UserRole } from '../enum';
import { ErrorBoundary } from '../errorBoundary';
import store from '../store';
import CapturePostHogPageViewComponent from './CapturePostHogPageView';
import { RequireAuth } from './RequireAuth';
import { SuspenseRoute } from './SuspenseRoute';

const AdminAddProductComponent = lazy(() => import('../components/pages/admin-add-product/admin-add-product'));
const AdminClientProfileComponent = lazy(() => import('../components/pages/client-profile/admin-client-profile'));
const AdminClientsComponent = lazy(() => import('../components/pages/admin-clients/admin-clients'));
const AdminClinicianComponent = lazy(() => import('../components/pages/admin-clinicians/admin-clinicians'));
const AdminJournalsComponent = lazy(() => import('../components/pages/admin-journals/admin-journals'));
const AdminOrganizationComponent = lazy(() => import('../components/pages/admin-organization/admin-organization'));
const AdminSiteComponent = lazy(() => import('../components/pages/admin-sites/admin-sites'));
const ClientProfileComponent = lazy(() => import('../components/pages/client-profile/client-profile'));
const ClientUpsertComponent = lazy(() => import('../components/pages/client-upsert/client-upsert'));
const ClinicianDashboardComponent = lazy(() => import('../components/pages/clinician-dashboard/clinician-dashboard'));
const ClinicianSettingsComponent = lazy(() => import('../components/pages/clinician-settings/clinician-settings'));
const DashboardComponent = lazy(() => import('../components/pages/dashboard/dashboard'));
const ForgotPasswordComponent = lazy(() => import('../components/pages/ForgotPassword/forgot'));
const InActiveClientComponent = lazy(() => import('../components/pages/inactive-clients/inactive-clients'));
const InterventionComponent = lazy(() => import('../components/pages/intervention/intervention'));
const LoginComponent = lazy(() => import('../components/pages/Login/login'));
const ProgramDashboardComponent = lazy(() => import('../components/pages/program-dashboard/program-dashboard'));
const ReportComponent = lazy(() => import('../components/pages/report/report'));
const ResetPasswordComponent = lazy(() => import('../components/pages/reset-password/reset-password'));
const ResourcesComponent = lazy(() => import('../components/pages/resources/resources'));
const ScheduledProductComponent = lazy(() => import('../components/pages/scheduled-products/scheduled-products'));

export const redirectPage = (currentUser: CurrentUser) => {
  if (currentUser.role === UserRole.ADMIN) {
    return '/page/report';
  } else if (currentUser.role === UserRole.PROGRAM_ADMINISTRATION) {
    return '/page/program-dashboard';
  } else {
    return '/page/home';
  }
};

const initialLoader = async () => {
  const currentUser = store.getState().authSlice.currentUser;
  return redirect(redirectPage(currentUser));
};

const RedirectFallback = () => <Spin style={{ marginTop: '25%' }} />;

const initialRoutes: RouteObject[] = [
  { index: true, loader: initialLoader, Component: RedirectFallback, HydrateFallback: RedirectFallback },
  { path: '*', loader: initialLoader, Component: RedirectFallback, HydrateFallback: RedirectFallback },
];

export const createRoute = (): RouteObject[] => {
  const authResult = store.getState().authSlice.authResult;

  const pageChildren: RouteObject[] = [
    ...initialRoutes,
    {
      path: 'home',
      element: (
        <SuspenseRoute>
          <RequireAuth requiredRoles={[UserRole.CLINICIAN, UserRole.ORGANIZATION, UserRole.SITE]}>
            <DashboardComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'client-profile/:client_id',
      element: (
        <SuspenseRoute>
          <RequireAuth requiredRoles={[UserRole.CLINICIAN, UserRole.ORGANIZATION, UserRole.SITE]}>
            <ClientProfileComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
      middleware: [
        ({ request }, next) => {
          if (!authResult.token) {
            const path = new URL(request.url).pathname;
            store.dispatch(setRedirectPath(path));
            next();
          }
        },
      ],
    },
    {
      path: 'admin-client-profile/:client_id',
      element: (
        <SuspenseRoute>
          <RequireAuth requiredRoles={[UserRole.ADMIN]}>
            <AdminClientProfileComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'admin-organizations',
      element: (
        <SuspenseRoute>
          <RequireAuth requiredRoles={[UserRole.ADMIN]}>
            <AdminOrganizationComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'admin-products',
      element: (
        <SuspenseRoute>
          <RequireAuth
            requiredRoles={[
              UserRole.ADMIN,
              UserRole.CLINICIAN,
              UserRole.ORGANIZATION,
              UserRole.SITE,
              UserRole.PROGRAM_ADMINISTRATION,
            ]}
          >
            <AdminJournalsComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'resources',
      element: (
        <SuspenseRoute>
          <RequireAuth
            requiredRoles={[
              UserRole.ADMIN,
              UserRole.CLINICIAN,
              UserRole.ORGANIZATION,
              UserRole.SITE,
              UserRole.PROGRAM_ADMINISTRATION,
            ]}
          >
            <ResourcesComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'clinician-settings',
      element: (
        <SuspenseRoute>
          <RequireAuth
            requiredRoles={[
              UserRole.CLINICIAN,
              UserRole.ORGANIZATION,
              UserRole.SITE,
              UserRole.ADMIN,
              UserRole.PROGRAM_ADMINISTRATION,
            ]}
          >
            <ClinicianSettingsComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'client-onboarding',
      element: (
        <SuspenseRoute>
          <RequireAuth
            requiredRoles={[
              UserRole.CLINICIAN,
              UserRole.ORGANIZATION,
              UserRole.SITE,
              UserRole.ADMIN,
              UserRole.PROGRAM_ADMINISTRATION,
            ]}
          >
            <ClientUpsertComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'program-dashboard',
      element: (
        <SuspenseRoute>
          <RequireAuth
            requiredRoles={[
              UserRole.PROGRAM_ADMINISTRATION,
              UserRole.ADMIN,
              UserRole.CLINICIAN,
              UserRole.ORGANIZATION,
              UserRole.SITE,
            ]}
          >
            <ProgramDashboardComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'program-dashboard-olh',
      element: (
        <SuspenseRoute>
          <RequireAuth
            requiredRoles={[
              UserRole.PROGRAM_ADMINISTRATION,
              UserRole.ADMIN,
              UserRole.CLINICIAN,
              UserRole.ORGANIZATION,
              UserRole.SITE,
            ]}
          >
            <ProgramDashboardOLHComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'scheduled-products',
      element: (
        <SuspenseRoute>
          <RequireAuth requiredRoles={[UserRole.ADMIN, UserRole.CLINICIAN, UserRole.SITE, UserRole.ORGANIZATION]}>
            <ScheduledProductComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'clinician-dashboard',
      element: (
        <SuspenseRoute>
          <RequireAuth requiredRoles={[UserRole.ADMIN, UserRole.ORGANIZATION]}>
            <ClinicianDashboardComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'interventions',
      element: (
        <SuspenseRoute>
          <RequireAuth requiredRoles={[UserRole.ADMIN, UserRole.ORGANIZATION]}>
            <InterventionComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'inactive-clients',
      element: (
        <SuspenseRoute>
          <RequireAuth requiredRoles={[UserRole.ADMIN, UserRole.ORGANIZATION]}>
            <InActiveClientComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'admin-clients',
      element: (
        <SuspenseRoute>
          <RequireAuth requiredRoles={[UserRole.ADMIN, UserRole.ORGANIZATION, UserRole.SITE]}>
            <AdminClientsComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'admin-clinicians',
      element: (
        <SuspenseRoute>
          <RequireAuth
            requiredRoles={[UserRole.ADMIN, UserRole.ORGANIZATION, UserRole.SITE, UserRole.PROGRAM_ADMINISTRATION]}
          >
            <AdminClinicianComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'admin-sites',
      element: (
        <SuspenseRoute>
          <RequireAuth requiredRoles={[UserRole.ADMIN, UserRole.ORGANIZATION]}>
            <AdminSiteComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'report',
      element: (
        <SuspenseRoute>
          <RequireAuth requiredRoles={[UserRole.ADMIN, UserRole.ORGANIZATION, UserRole.PROGRAM_ADMINISTRATION]}>
            <ReportComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'clients',
      element: (
        <SuspenseRoute>
          <RequireAuth requiredRoles={[UserRole.CLINICIAN]}>
            <AdminClientsComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
    {
      path: 'admin-add-product',
      element: (
        <SuspenseRoute>
          <RequireAuth requiredRoles={[UserRole.ADMIN]}>
            <AdminAddProductComponent />
          </RequireAuth>
        </SuspenseRoute>
      ),
    },
  ];

  const filteredPageChildren = pageChildren.filter((c) => {
    switch (c?.path) {
      case 'scheduled-products':
        return authResult.use_scheduler;
      case 'clinician-dashboard':
        return authResult.use_clinician_dashboard;
      default:
        return true;
    }
  });

  return [
    ...initialRoutes,
    {
      path: '/login',
      element: (
        <SuspenseRoute>
          <CapturePostHogPageViewComponent>
            <LoginComponent />
          </CapturePostHogPageViewComponent>
        </SuspenseRoute>
      ),
    },
    {
      path: '/forgot-password',
      element: (
        <SuspenseRoute>
          <CapturePostHogPageViewComponent>
            <ForgotPasswordComponent />
          </CapturePostHogPageViewComponent>
        </SuspenseRoute>
      ),
    },
    {
      path: '/auth/reset/:token',
      element: (
        <SuspenseRoute>
          <CapturePostHogPageViewComponent>
            <ResetPasswordComponent />
          </CapturePostHogPageViewComponent>
        </SuspenseRoute>
      ),
      loader: resetPasswordLoaderWithStore(store),
    },
    {
      path: '/page',
      element: (
        <ErrorBoundary>
          <RequireAuth
            requiredRoles={[
              UserRole.ADMIN,
              UserRole.CLINICIAN,
              UserRole.ORGANIZATION,
              UserRole.SITE,
              UserRole.PROGRAM_ADMINISTRATION,
            ]}
          >
            <CapturePostHogPageViewComponent>
              <MainLayout />
            </CapturePostHogPageViewComponent>
          </RequireAuth>
        </ErrorBoundary>
      ),
      children: filteredPageChildren,
    },
  ];
};
